import { useLazyQuery, useMutation } from "@apollo/client";
import { useContext, useEffect, useState } from "react";
import { ConvertToExtendedTicket, isTicketPastRetention, TicketDatabase } from "../Utils/TicketDatabase";
import { TicketExtended } from "../Utils/TypeExtensions";
import { DELETE_TICKET, getTicketList } from "../sharedCode/queries";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import dayjs from "dayjs";
import { validateMergedTicket, validateServerResponse } from "../Utils/TicketComparer";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { DUPLICATE_TICKET, PARTIAL_SYNC } from "../Utils/TicketValidator";
import { mergeLocalTicketWithServerResult } from "../Utils/TicketMerger";
import { GlobalContext } from "../Utils/Contexts";
import { logSentry } from "../Utils/Sentry";

// NB - this does not work in strict mode (Apollo gets confused), make sure to disable it in index.tsx before debugging
export default function useDuplicateTicketResolver(props: BackgroundProps): TableSyncer {
    const context = useContext(GlobalContext);
    const [status, setStatus] = useState(SyncStatus.Fresh);

    const [doQuery, { called: called, loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getTicketList, {
        fetchPolicy: "no-cache",
        nextFetchPolicy: "no-cache",
    });
    const [deleteTicket] = useMutation(DELETE_TICKET, { fetchPolicy: "no-cache" });

    const fetchData = async () => {
        if (context.loggedInUser == null || context.loggedInUser == undefined || context.loggedInUser.id == null || context.loggedInUser.id == undefined) {
            // haven't loaded a user yet, bail out
            return;
        }

        const duplicates = await TicketDatabase.ticket.filter((t) => t.pushAttempts === DUPLICATE_TICKET).toArray();

        if (duplicates.length > 0) {
            setStatus(SyncStatus.Loading);

            if (!called) {
                await doQuery({ variables: { pilotId: context.loggedInUser.id } });
            } else {
                props.updateUi();

                await refetch({ pilotId: context.loggedInUser.id })
                    .then((results) => {
                        mergeServerTicketWithLocalCopy(results.data);
                    })
                    .then(() => {
                        setStatus(SyncStatus.Fresh);
                        props.updateUi();
                    })
                    .catch((e) => {
                        setStatus(SyncStatus.Stale);
                        props.updateUi();
                    });
            }
        }
    };

    async function mergeServerTicketWithLocalCopy(result: any) {
        let syncManagerStatus = SyncStatus.Fresh; // TODO - should probably have a heirarchy of statuses (i.e. if it's error don't go back to stale)

        const duplicates = await TicketDatabase.ticket.filter((t) => t.pushAttempts === DUPLICATE_TICKET).toArray();
        const serverTickets = (result.getTicketsByPersonId?.slice() ?? []) as TicketExtended[];

        const listToSave = [] as TicketExtended[];
        const localIdsToDelete = [] as number[];

        for (let i = 0; i < duplicates.length; i++) {
            const localTicket = duplicates[i];

            // have to look up by business key because a partially synced ticket will have a new server ID
            const serverTicket = serverTickets.find((t) => {
                return t.job_id === localTicket.job_id && dayjs(t.date).isSame(dayjs(localTicket.date), "day") && t.revision_num === localTicket.revision_num;
            });

            if (!serverTicket) {
                // there's nothing on the server so just reset pushAttempts so the ticket is pushed on the next sync attempt
                syncManagerStatus = SyncStatus.Stale;
                listToSave.push({ ...localTicket, isDuplicateTicket: false, pushAttempts: 0 });
                localIdsToDelete.push(localTicket.id);
            } else {
                const differences = validateServerResponse(localTicket, serverTicket);

                if (Object.keys(differences).length === 0) {
                    // lossless transmission, OK to replace local copy
                    listToSave.push(ObjectUtils.convertToUppercase(await ConvertToExtendedTicket(serverTicket)) as TicketExtended);
                    localIdsToDelete.push(localTicket.id);
                } else {
                    // add back missing local data and flag for syncing
                    const mergedTicket = mergeLocalTicketWithServerResult(localTicket, serverTicket);
                    const mergedDiff = validateMergedTicket(localTicket, serverTicket, mergedTicket);

                    // make sure we don't have any differences after merging
                    if (Object.keys(mergedDiff).length === 0) {
                        listToSave.push({ ...mergedTicket, isDuplicateTicket: false, hasLocalChanges: true, pushAttempts: 0 });
                        localIdsToDelete.push(localTicket.id);
                    } else {
                        logSentry("FAILED_DUPLICATE_MERGE", `${localTicket.ticket_num} | ${JSON.stringify(mergedDiff)}`);
                        syncManagerStatus = SyncStatus.Error;
                        listToSave.push({ ...localTicket, isDuplicateTicket: false, pushAttempts: PARTIAL_SYNC });
                        localIdsToDelete.push(localTicket.id);
                    }
                }
            }
        }

        const listToSaveWithinRetention = listToSave.filter((ticket) => !isTicketPastRetention(ticket.date));

        TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
            await TicketDatabase.ticket.bulkDelete(localIdsToDelete).then(() => TicketDatabase.ticket.bulkAdd(listToSaveWithinRetention));
        }) // end of transaction wrapper
            .then(() => {
                setStatus(syncManagerStatus);
            })
            .catch((e) => {
                setStatus(SyncStatus.Error);
            });
    }

    useEffect(() => {
        if (gqlResult && gqlResult.getTicketsByPersonId) {
            mergeServerTicketWithLocalCopy(gqlResult).then(() => {
                setStatus(SyncStatus.Fresh);
                props.updateUi();
            });
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            setStatus(SyncStatus.Stale);
            props.updateUi();
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
